import { useWarehouse } from "./useWarehouse";
import { useSteps } from "./useSteps";

import { PackageLookup } from "./PackageLookup";
import { CapturePackagePhotos } from "./CapturePackagePhotos";
import { CheckContents } from "./CheckContents";
import { ControlWeightCapture } from "./ControlWeightCapture";
import { PackagingWeightCapture } from "./PackagingWeightCapture";
import { ContentWeightCapture } from "./ContentWeightCapture";
import { NonConformPhoto } from "./NonConformPhoto";
import { SortContents } from "./SortContents";
import { AssembledWeightCapture } from "./AssembledWeightCapture";
import { ConformingWeightCapture } from "./ConformingWeightCapture";
import { CaptureScaleReadingPhoto } from "./CaptureScaleReadingPhoto";
import { Summary } from "./Summary";
import { ProcessingIssues } from "./ProcessingIssues";
import { CheckPackaging } from "./CheckPackaging";
import { CaptureDamagedPackagingPhotos } from "./CaptureDamagedPackagingPhotos";
const getFlags = (takeback) => ({
  LEGO: takeback?.product_id === "lego-bricks",
});

export const useConfiguration = () => ({
  isStepVisible: (step) => isStepVisible(step),
  isStepValid: (step) => isStepValid(step),
  stepsRequiredOnCheckIn: () => stepsRequiredOnCheckIn(),
});

export const isStepValid = (step) => {
  const {
    packagePictures,
    requiresBoxDamageEvidence,
    hasCapturedBoxDamageEvidence,
    hasCheckedContents,
    controlWeight,
    packagingWeight,
    contentWeight,
    sortedPictures,
    assembledWeight,
    conformingWeight,
    scaleReadingPictures,
    nonConformPictures,
  } = useWarehouse.getState();

  if (step === ProcessingIssues || step === PackageLookup) {
    return true;
  }

  if (step === CapturePackagePhotos) {
    return packagePictures.length > 0;
  }

  if (step === CaptureDamagedPackagingPhotos) {
    return !requiresBoxDamageEvidence || hasCapturedBoxDamageEvidence;
  }

  if (step === CheckPackaging) {
    // once the content has been checked (Check In flow) the no need to do these steps again
    return hasCheckedContents;
  }

  if (step === CheckContents) {
    return hasCheckedContents;
  }
  if (step === ControlWeightCapture) {
    return controlWeight !== "";
  }
  if (step === PackagingWeightCapture) {
    return packagingWeight !== "";
  }
  if (step === ContentWeightCapture) {
    return contentWeight !== "";
  }
  if (step === NonConformPhoto) {
    return nonConformPictures.length > 0; // Required - must have at least one photo
  }
  if (step === SortContents) {
    return sortedPictures.all !== undefined;
  }
  if (step === AssembledWeightCapture) {
    return assembledWeight !== "";
  }
  if (step === ConformingWeightCapture) {
    return conformingWeight !== "";
  }
  if (step === CaptureScaleReadingPhoto) {
    return scaleReadingPictures.length > 0;
  }
  if (step === Summary) {
    if (
      contentWeight !== "" &&
      conformingWeight !== "" &&
      controlWeight !== ""
    ) {
      const actualContentWeight = Number(contentWeight) - Number(controlWeight);
      const actualConformingWeight =
        Number(conformingWeight) - Number(controlWeight);
      const actualNonConformingWeight =
        actualContentWeight - actualConformingWeight;

      if (actualConformingWeight < 0) return false;
      if (actualNonConformingWeight < 0) return false;

      if (actualConformingWeight > actualContentWeight) return false;
      if (actualNonConformingWeight > actualContentWeight) return false;
    }

    return true;
  }

  return false;
};

export const getInvalidStepReason = (step, data) => {
  if (step === CapturePackagePhotos) {
    return "Missing package photo";
  }

  if (step === CaptureDamagedPackagingPhotos) {
    return "Missing damaged packaging photo";
  }

  if (step === ControlWeightCapture) {
    return "Missing control weight";
  }

  if (step === PackagingWeightCapture) {
    return "Missing packaging weight";
  }

  if (step === ContentWeightCapture) {
    return "Missing content weight";
  }

  if (step === NonConformPhoto) {
    return "Missing non-conform photos";
  }

  if (step === SortContents) {
    return "Missing sorted photos";
  }

  if (step === AssembledWeightCapture) {
    return "Missing assembled weight";
  }

  if (step === ConformingWeightCapture) {
    return "Missing conforming weight";
  }

  if (step === CaptureScaleReadingPhoto) {
    return "Missing scale reading photo";
  }

  if (step === Summary) {
    const { controlWeight, contentWeight, conformingWeight } = data;

    const actualContentWeight = Number(contentWeight) - Number(controlWeight);
    const actualConformingWeight =
      Number(conformingWeight) - Number(controlWeight);
    const actualNonConformingWeight =
      actualContentWeight - actualConformingWeight;

    const reasons = [];

    if (actualConformingWeight < 0) {
      reasons.push("Conforming weight is negative");
    }

    if (actualNonConformingWeight < 0) {
      reasons.push("Non-conforming weight is negative");
    }

    if (actualConformingWeight > actualContentWeight) {
      reasons.push("Conforming weight is greater than content weight");
    }

    if (actualNonConformingWeight > actualContentWeight) {
      reasons.push("Non-conforming weight is greater than content weight");
    }

    return reasons.join(", ");
  }

  return "";
};

export const isStepVisible = (step) => {
  const { takeback } = useWarehouse.getState();
  const { dropStoreMode } = useSteps.getState();

  if (!takeback) {
    return false;
  }

  // PackagingWeightCapture is only visible in Drop Store mode
  if (step === PackagingWeightCapture && !dropStoreMode) {
    return false;
  }

  // CheckContents is only visible in Mail-in mode (not in Drop Store mode)
  if (step === CheckContents && dropStoreMode) {
    return false;
  }

  if (getFlags(takeback).LEGO && [AssembledWeightCapture].includes(step)) {
    return false;
  }

  return true;
};

export const stepsRequiredOnCheckIn = (takeback) => {
  const currentTakeback =
    takeback || useWarehouse.getState((state) => state.takeback);
  const { dropStoreMode } = useSteps.getState();

  if (!currentTakeback) {
    return [];
  }
  if (getFlags(currentTakeback).LEGO) {
    // In drop store mode, don't include CheckContents
    if (dropStoreMode) {
      return [
        CapturePackagePhotos,
        CheckPackaging,
        CaptureDamagedPackagingPhotos,
      ];
    }
    return [
      CapturePackagePhotos,
      CheckPackaging,
      CaptureDamagedPackagingPhotos,
      CheckContents,
    ];
  }
  return [];
};
